import { Controller } from "@hotwired/stimulus"

/**
 * Report Wizard Controller - Orchestrates the multi-tab report form
 * Coordinates tabs, autosave, form state, and navigation
 * Connects with outlets: tabs, autosave, form-state
 */
export default class extends Controller {
  static targets = ["form", "progress", "prevButton", "nextButton", "submitButton"]
  static outlets = ["tabs", "autosave", "form-state"]
  static values = {
    reportId: Number,
    taskId: Number,
    progressPercentage: { type: Number, default: 0 }
  }

  connect() {
    console.log('[report-wizard] Controller connected', {
      reportId: this.reportIdValue,
      taskId: this.taskIdValue
    })

    // Initialize progress bar
    this.updateProgressBar()

    // Update navigation buttons
    this.updateNavigationButtons()

    // Listen for tab changes
    this.element.addEventListener('tabs:change', this.handleTabChange.bind(this))

    // Listen for BEFORE tab changes to trigger saves
    this.element.addEventListener('tabs:before-change', this.handleBeforeTabChange.bind(this))
  }

  disconnect() {
    console.log('[report-wizard] Controller disconnected')
    this.element.removeEventListener('tabs:change', this.handleTabChange.bind(this))
    this.element.removeEventListener('tabs:before-change', this.handleBeforeTabChange.bind(this))
  }

  /**
   * Handle BEFORE tab change event to trigger saves
   * @param {CustomEvent} event - Before tab change event
   */
  async handleBeforeTabChange(event) {
    console.log('┌───────────────────────────────────────────┐')
    console.log('│   REPORT WIZARD: BEFORE TAB CHANGE        │')
    console.log('└───────────────────────────────────────────┘')

    const { fromIndex, toIndex, fromPanel } = event.detail
    console.log('[report-wizard] Tab change:', fromIndex, '->', toIndex)
    console.log('[report-wizard] From panel:', fromPanel?.id)
    console.log('[report-wizard] Has autosave outlet:', this.hasAutosaveOutlet)

    // Save current tab data BEFORE switching
    try {
      // Check if switching FROM Additional Pages tab (index 2)
      if (fromIndex === 2) {
        console.log('[report-wizard] Detected Additional Pages tab, saving...')
        await this.saveAdditionalPages(fromPanel)
        console.log('[report-wizard] Additional Pages saved')
      }

      // Check if there are unsaved changes before saving
      const isDirty = this.hasFormStateOutlet && this.formStateOutlet.isDirty()
      console.log('[report-wizard] Form is dirty:', isDirty)

      if (!isDirty) {
        console.log('[report-wizard] No changes to save, proceeding with tab switch')
      } else {
        // For all other tabs, use the autosave controller
        if (this.hasAutosaveOutlet) {
          console.log('[report-wizard] Has autosave outlet, calling saveNow()...')
          await this.autosaveOutlet.saveNow()
          console.log('[report-wizard] Autosave saveNow() completed')
        } else {
          console.error('[report-wizard] NO AUTOSAVE OUTLET AVAILABLE!')
          console.log('[report-wizard] Available outlets:', Object.keys(this).filter(k => k.includes('Outlet')))
        }
      }

      console.log('[report-wizard] All saves complete, tab switch can proceed')
    } catch (error) {
      console.error('[report-wizard] ERROR saving before tab switch:', error)
      console.error('[report-wizard] Error stack:', error.stack)
      // Continue with tab switch even if save fails
    }
  }

  /**
   * Save Additional Pages before leaving that tab
   * @param {Element} panel - Additional pages panel element
   */
  async saveAdditionalPages(panel) {
    // Find the additional-pages controller in the panel
    const additionalPagesElement = panel.querySelector('[data-controller*="additional-pages"]')
    if (!additionalPagesElement) {
      console.log('[report-wizard] No additional-pages controller found')
      return
    }

    // Get the Stimulus controller instance
    const application = this.application
    const additionalPagesController = application.getControllerForElementAndIdentifier(
      additionalPagesElement,
      'additional-pages'
    )

    if (additionalPagesController && typeof additionalPagesController.savePage === 'function') {
      console.log('[report-wizard] Calling additional-pages savePage()')
      await additionalPagesController.savePage()
    }
  }

  /**
   * Handle tab change event
   * @param {CustomEvent} event - Tab change event
   */
  handleTabChange(event) {
    const { index } = event.detail
    console.log('[report-wizard] Tab changed to:', index)

    // Update navigation buttons
    this.updateNavigationButtons()

    // Update progress bar
    this.updateProgressBar()

    // Initialize content for the new tab if needed
    this.initializeTabContent(index)
  }

  /**
   * Navigate to the previous tab
   * @param {Event} event - Click event
   */
  async previousTab(event) {
    event.preventDefault()

    // Save if there are unsaved changes
    if (this.hasFormStateOutlet && this.formStateOutlet.isDirty()) {
      console.log('[report-wizard] Saving before previous tab...')
      if (this.hasAutosaveOutlet) {
        await this.autosaveOutlet.saveNow()
      }
    } else {
      console.log('[report-wizard] No changes, switching to previous tab immediately')
    }

    // Navigate to previous tab
    if (this.hasTabsOutlet) {
      this.tabsOutlet.previous()
    }
  }

  /**
   * Navigate to the next tab
   * @param {Event} event - Click event
   */
  async nextTab(event) {
    event.preventDefault()

    // Validate current tab before proceeding
    const isValid = await this.validateCurrentTab()
    if (!isValid) {
      this.showValidationError()
      return
    }

    // Save only if there are unsaved changes
    if (this.hasFormStateOutlet && this.formStateOutlet.isDirty()) {
      console.log('[report-wizard] Saving before next tab...')
      if (this.hasAutosaveOutlet) {
        await this.autosaveOutlet.saveNow()
      }
    } else {
      console.log('[report-wizard] No changes, switching to next tab immediately')
    }

    // Navigate to next tab
    if (this.hasTabsOutlet) {
      this.tabsOutlet.next()
    }
  }

  /**
   * Submit the form (final save)
   * @param {Event} event - Click event
   */
  async submitForm(event) {
    event.preventDefault()

    // Validate all tabs
    const isValid = await this.validateAllTabs()
    if (!isValid) {
      this.showValidationError('Please complete all required fields')
      return
    }

    // Mark form as clean to prevent unsaved changes warning
    if (this.hasFormStateOutlet) {
      this.formStateOutlet.markClean()
    }

    // Submit the form
    if (this.hasFormTarget) {
      this.formTarget.requestSubmit()
    }
  }

  /**
   * Validate the current tab
   * @returns {Promise<boolean>} True if valid
   */
  async validateCurrentTab() {
    if (!this.hasTabsOutlet) return true

    const currentPanel = this.tabsOutlet.currentPanel
    if (!currentPanel) return true

    // Find required fields in current panel
    const requiredFields = currentPanel.querySelectorAll('input[required], select[required], textarea[required]')

    let allValid = true
    requiredFields.forEach(field => {
      if (!field.validity.valid) {
        field.reportValidity()
        allValid = false
      }
    })

    return allValid
  }

  /**
   * Validate all tabs
   * @returns {Promise<boolean>} True if all valid
   */
  async validateAllTabs() {
    if (!this.hasFormTarget) return true

    const requiredFields = this.formTarget.querySelectorAll('input[required], select[required], textarea[required]')

    let allValid = true
    requiredFields.forEach(field => {
      if (!field.validity.valid) {
        allValid = false
      }
    })

    return allValid
  }

  /**
   * Confirm unsaved changes with user
   * @returns {Promise<boolean>} True if user wants to continue
   */
  confirmUnsavedChanges() {
    return new Promise((resolve) => {
      const confirmed = confirm(
        'You have unsaved changes. Do you want to continue without saving?'
      )
      resolve(confirmed)
    })
  }

  /**
   * Show validation error message
   * @param {string} message - Error message
   */
  showValidationError(message = 'Please fill in all required fields in this section') {
    // You can customize this to show a toast notification or inline message
    alert(message)
  }

  /**
   * Update navigation buttons state
   */
  updateNavigationButtons() {
    if (!this.hasTabsOutlet) return

    const isFirst = this.tabsOutlet.isFirstTab
    const isLast = this.tabsOutlet.isLastTab

    // Update Previous button
    if (this.hasPrevButtonTarget) {
      this.prevButtonTarget.disabled = isFirst
      this.prevButtonTarget.classList.toggle('opacity-50', isFirst)
    }

    // Update Next button
    if (this.hasNextButtonTarget) {
      this.nextButtonTarget.classList.toggle('hidden', isLast)
    }

    // Update Submit button
    if (this.hasSubmitButtonTarget) {
      this.submitButtonTarget.classList.toggle('hidden', !isLast)
    }
  }

  /**
   * Update progress bar
   */
  updateProgressBar() {
    if (!this.hasProgressTarget) return

    // Calculate progress based on current tab
    const totalTabs = this.hasTabsOutlet ? this.tabsOutlet.tabTargets.length : 5
    const currentTab = this.hasTabsOutlet ? this.tabsOutlet.indexValue : 0
    const tabProgress = ((currentTab + 1) / totalTabs) * 100

    // Update progress bar
    this.progressTarget.style.width = `${tabProgress}%`
    this.progressTarget.setAttribute('aria-valuenow', tabProgress)

    console.log('[report-wizard] Progress updated:', tabProgress)
  }

  /**
   * Initialize tab content (e.g., lazy load data, init widgets)
   * @param {number} tabIndex - Index of the tab
   */
  initializeTabContent(tabIndex) {
    if (!this.hasTabsOutlet) return

    const panel = this.tabsOutlet.panelTargets[tabIndex]
    if (!panel) return

    // Check if panel contains a turbo-frame that needs loading
    const turboFrame = panel.querySelector('turbo-frame[loading="lazy"]')
    if (turboFrame && !turboFrame.hasAttribute('complete')) {
      console.log('[report-wizard] Loading lazy frame for tab:', tabIndex)
    }

    // Dispatch custom event for tab-specific initialization
    this.dispatch('tab-initialized', {
      detail: { tabIndex, panel }
    })
  }

  /**
   * Handle form submission success
   * @param {Event} event - Turbo submit end event
   */
  handleSubmitSuccess(event) {
    if (event.detail.success) {
      console.log('[report-wizard] Form submitted successfully')

      // Mark form as clean
      if (this.hasFormStateOutlet) {
        this.formStateOutlet.markClean()
      }

      // Optionally show success message
      this.showSuccessMessage()
    }
  }

  /**
   * Show success message
   */
  showSuccessMessage() {
    // You can customize this to show a toast notification
    console.log('[report-wizard] Report saved successfully')
  }

  /**
   * Get current tab index
   * @returns {number} Current tab index
   */
  get currentTabIndex() {
    return this.hasTabsOutlet ? this.tabsOutlet.indexValue : 0
  }

  /**
   * Check if wizard is complete
   * @returns {boolean} True if on last tab
   */
  get isComplete() {
    return this.hasTabsOutlet ? this.tabsOutlet.isLastTab : false
  }
}
